import React from 'react';
import { XIcon, AlertCircleIcon, CheckCircleIcon } from 'lucide-react';
interface BackdatedLessonWarningModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  isDark: boolean;
  lessonDate: Date;
}
export const BackdatedLessonWarningModal: React.FC<BackdatedLessonWarningModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  isDark,
  lessonDate
}) => {
  if (!isOpen) return null;
  // Format the lesson date for display
  const formattedDate = lessonDate.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
  const formattedTime = lessonDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/70 z-[60] backdrop-blur-sm" onClick={onCancel} aria-hidden="true" data-id="element-1967" />

      {/* Modal */}
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-1968">
        <div className={`${isDark ? 'bg-navy-800' : 'bg-white'} rounded-xl shadow-2xl max-w-md w-full p-6`} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="backdated-lesson-title" data-id="element-1969">
          {/* Icon and Title */}
          <div className="flex items-start mb-4" data-id="element-1970">
            <div className={`p-3 rounded-full mr-4 ${isDark ? 'bg-yellow-500/20' : 'bg-yellow-50'}`} data-id="element-1971">
              <AlertCircleIcon className={`h-6 w-6 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} data-id="element-1972" />
            </div>
            <div className="flex-1" data-id="element-1973">
              <h3 id="backdated-lesson-title" className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-1974">
                Creating a Past Lesson
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-1975">
                You're creating a lesson in the past
              </p>
            </div>
            <button onClick={onCancel} className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} transition-colors`} aria-label="Close" data-id="element-1976">
              <XIcon className="h-5 w-5" data-id="element-1977" />
            </button>
          </div>

          {/* Lesson Date Info */}
          <div className={`p-4 rounded-lg mb-4 ${isDark ? 'bg-navy-700' : 'bg-gray-50'}`} data-id="element-1978">
            <p className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1979">
              Lesson Date & Time
            </p>
            <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-1980">
              {formattedDate}
            </p>
            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-1981">
              {formattedTime}
            </p>
          </div>

          {/* Warning Message */}
          <div className={`p-4 rounded-lg mb-6 border-2 ${isDark ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-yellow-50 border-yellow-200'}`} data-id="element-1982">
            <p className={`text-sm ${isDark ? 'text-yellow-300' : 'text-yellow-800'}`} data-id="element-1983">
              <strong data-id="element-1984">This is usually used to log a completed lesson.</strong>{' '}
              Creating lessons in the past can affect your reports and
              analytics.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col-reverse sm:flex-row gap-3" data-id="element-1985">
            <button onClick={onCancel} className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${isDark ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'}`} data-id="element-1986">
              Cancel
            </button>
            <button onClick={onConfirm} className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${isDark ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 border-2 border-yellow-500/30' : 'bg-yellow-500 hover:bg-yellow-600 text-white'}`} data-id="element-1987">
              <CheckCircleIcon className="h-4 w-4" data-id="element-1988" />
              Continue Anyway
            </button>
          </div>

          {/* Helper Text */}
          <p className={`text-xs text-center mt-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-1989">
            You can mark this lesson as completed after creating it
          </p>
        </div>
      </div>
    </>;
};